import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getSRStats, SRStats } from '../../services/api';

interface MemoryStrengthWidgetProps {
  studentId: string;
}

export const MemoryStrengthWidget: React.FC<MemoryStrengthWidgetProps> = ({ studentId }) => {
  const [stats, setStats] = useState<SRStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSRStats(studentId)
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) {
    return (
      <div className="memory-strength-widget loading">
        <div className="loading-pulse" />
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  // Calculate memory strength as a percentage
  // Based on: cards in review state / total cards, weighted by retention rate
  const totalActive = stats.total_cards - stats.new_cards;
  const strongMemories = stats.review_cards; // Cards in stable "review" state
  const memoryStrength = totalActive > 0
    ? Math.round((strongMemories / totalActive) * 100)
    : 0;

  // Determine status and color
  let statusLabel = 'Building';
  let statusColor = '#f59e0b'; // amber
  if (memoryStrength >= 80) {
    statusLabel = 'Strong';
    statusColor = '#22c55e'; // green
  } else if (memoryStrength >= 50) {
    statusLabel = 'Growing';
    statusColor = '#3b82f6'; // blue
  }

  // Calculate retention display
  const retentionDisplay = stats.retention_rate_30d
    ? `${Math.round(stats.retention_rate_30d * 100)}%`
    : '--';

  // Determine if reviews are needed
  const hasReviews = stats.due_today > 0;

  return (
    <div className="memory-strength-widget">
      <div className="memory-header">
        <span className="memory-icon">&#128161;</span>
        <h4>Memory Strength</h4>
      </div>

      <div className="memory-stats">
        <div className="memory-main-stat">
          <div
            className="memory-ring"
            style={{
              background: `conic-gradient(${statusColor} ${memoryStrength * 3.6}deg, #e5e7eb ${memoryStrength * 3.6}deg)`
            }}
          >
            <div className="memory-ring-inner">
              <span className="memory-percent">{memoryStrength}%</span>
              <span className="memory-label">{statusLabel}</span>
            </div>
          </div>
        </div>

        <div className="memory-details">
          <div className="memory-detail-row">
            <span className="detail-label">Questions learned</span>
            <span className="detail-value">{stats.total_cards}</span>
          </div>
          <div className="memory-detail-row">
            <span className="detail-label">30-day retention</span>
            <span className="detail-value">{retentionDisplay}</span>
          </div>
          <div className="memory-detail-row">
            <span className="detail-label">Total reviews</span>
            <span className="detail-value">{stats.total_reviews}</span>
          </div>
        </div>
      </div>

      {hasReviews ? (
        <Link to="/practice?mode=review_only" className="memory-cta has-reviews">
          <span className="cta-count">{stats.due_today}</span>
          <span className="cta-text">reviews due today</span>
          <span className="cta-arrow">&#8594;</span>
        </Link>
      ) : (
        <div className="memory-cta all-done">
          <span className="cta-icon">&#10003;</span>
          <span className="cta-text">All reviews complete!</span>
        </div>
      )}

      <p className="memory-tip">
        Regular reviews strengthen long-term memory. Complete reviews daily for best results.
      </p>
    </div>
  );
};
